import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { StyledDocument } from "./ProposalPreviewFrom.styled";
import { useDispatch, useSelector } from "react-redux";
import ProposalPreviewFormHeader from "./ProposalPreviewFormHeader";
import ProposalPreviewFormFooter from "./ProposalPreviewFormFooter";
import { CleaningBidCalculator, CleaningPricingSection } from "./ProposalProjectType";
import { PROPOSAL_SET_VALUES } from "../../store/constants/proposalConstants";
import { PaintPricingSection } from "../PaintProposalPreviewForm/ProposalTemplate";
import SortableSection from "./SortableSection";
import SignatureBlock from "./SignatureBlock";
import SimpleRichTextEditor from "./SimpleTextEditor";
import PaintSimpleRichTextEditor from "../PaintProposalPreviewForm/SimpleTextEditor";
import {
  ClientSection,
  ProjectInfoSection,
  ProjectTeamSection,
  SubmittedBySection,
} from "./page1Sections";
import {
  normalizeSectionOrder,
  reorderList,
} from "../../utils/constants/proposalSectionOrder";

const ProposalPreviewForm = ({ disable_fields }) => {
  const {
    project_name,
    project_address,
    project_street,
    project_city,
    project_state,
    project_zip,
    bid_amount,
    project_contact_1_name,
    project_contact_1_phone,
    project_contact_1_email,
    project_contact_2_name,
    project_contact_2_email,
    project_contact_2_phone,
    project_contact_3_name,
    project_contact_3_email,
    project_contact_3_phone,
    project_contact_4_name,
    project_contact_4_email,
    project_contact_4_phone,
    customer_company_name,
    customer_street,
    customer_state,
    customer_city,
    customer_zip,
    customer_address,
    border_color,
    section_order,
  } = useSelector((state) => state.proposalValues);

  const [ proposalValues, setProposalValues ] = useState({});
  const userDetails = useSelector((state) => state.userDetails);
  const sections = useSelector((state) => state.proposalSections);
  const current_date = new Date().toUTCString().slice(0, 16);
  const { user } = userDetails;
  const sectionOrder = normalizeSectionOrder(section_order);

  const dispatch = useDispatch();

  const handleChange = (evt) => {
    const { name, value: newValue } = evt?.target || evt;
    dispatch({ type: PROPOSAL_SET_VALUES, payload: { [name]: newValue } });
    setProposalValues((prev) =>({
      ...prev,
      [name]: newValue,
    }));
  };

  useEffect(() => {
    setProposalValues({
      project_name: project_name,
      project_address: project_address,
      project_street: project_street,
      project_city: project_city,
      project_state: project_state,
      project_zip: project_zip,
      bid_amount: bid_amount,
      project_contact_1_name: project_contact_1_name,
      project_contact_1_phone: project_contact_1_phone,
      project_contact_1_email: project_contact_1_email,
      project_contact_2_name: project_contact_2_name,
      project_contact_2_email: project_contact_2_email,
      project_contact_2_phone: project_contact_2_phone,
      project_contact_3_name: project_contact_3_name,
      project_contact_3_email: project_contact_3_email,
      project_contact_3_phone: project_contact_3_phone,
      project_contact_4_name: project_contact_4_name,
      project_contact_4_email: project_contact_4_email,
      project_contact_4_phone: project_contact_4_phone,
      customer_company_name: customer_company_name,
      customer_street: customer_street,
      customer_state: customer_state,
      customer_city: customer_city,
      customer_zip: customer_zip,
      customer_address: customer_address,
      border_color: border_color
    })
  },[
    project_name,
    project_address,
    project_street,
    project_city,
    project_state,
    project_zip,
    bid_amount,
    project_contact_1_name,
    project_contact_1_phone,
    project_contact_1_email,
    project_contact_2_name,
    project_contact_2_email,
    project_contact_2_phone,
    project_contact_3_name,
    project_contact_3_email,
    project_contact_3_phone,
    project_contact_4_name,
    project_contact_4_email,
    project_contact_4_phone,
    customer_company_name,
    customer_street,
    customer_state,
    customer_city,
    customer_zip,
    customer_address,
    border_color
  ]);

  const resetValue = (name) => {
    dispatch({ type: PROPOSAL_SET_VALUES, payload: { [name]: "" } });
    setProposalValues((prev) => ({
      ...prev,
      [name]: ""
    }));
  };

  useEffect(() => {
    if(!sections.project_address){
      resetValue("project_address");

    } else if (!sections.client_name){
      resetValue("customer_company_name");

    } else if (!sections.client_address) {
      resetValue("customer_address");

    }

  },[sections])

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) {
      return;
    }
    if (source.index === destination.index) {
      return;
    }
    dispatch({
      type: PROPOSAL_SET_VALUES,
      payload: {
        section_order: reorderList(
          sectionOrder,
          source.index,
          destination.index
        ),
      },
    });
  };

  const isPainting = user?.trade === "painting";

  const renderSection = (id) => {
    const sharedProps = {
      sections,
      proposalValues,
      handleChange,
      disable_fields,
      user,
      current_date,
    };
    switch (id) {
      case "submitted_by":
        return <SubmittedBySection {...sharedProps} />;
      case "project_info":
        return <ProjectInfoSection {...sharedProps} />;
      case "client":
        return <ClientSection {...sharedProps} />;
      case "project_team":
        return <ProjectTeamSection {...sharedProps} />;
      case "scope_of_work":
        return isPainting ? (
          <PaintSimpleRichTextEditor disable_fields={disable_fields} />
        ) : (
          <SimpleRichTextEditor disable_fields={disable_fields} />
        );
      case "pricing":
        return isPainting ? (
          <PaintPricingSection disable_fields={disable_fields} />
        ) : (
          <CleaningPricingSection disable_fields={disable_fields} />
        );
      case "signature_block":
        return <SignatureBlock disable_fields={disable_fields} />;
      default:
        return null;
    }
  };

  return (
    <StyledDocument>
      {!disable_fields && (
        <p className="text-muted mb-2" style={{ fontSize: 13 }}>
          Drag the grip handles to rearrange sections, or use Arrange Sections in the form.
        </p>
      )}
      <CleaningBidCalculator />
      <DragDropContext onDragEnd={onDragEnd}>
        <section className="bg-white p-5" style={proposalValues?.border_color && proposalValues?.border_color !== "null" ? { border: `2px solid ${proposalValues?.border_color}` } : {}}>
          <ProposalPreviewFormHeader disable_fields={disable_fields} />
          <Droppable droppableId="proposal-sections" isDropDisabled={!!disable_fields}>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="proposal-page-sections"
              >
                {sectionOrder.map((id, index) => (
                  <SortableSection
                    key={id}
                    id={id}
                    index={index}
                    disabled={disable_fields}
                  >
                    {renderSection(id)}
                  </SortableSection>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          <ProposalPreviewFormFooter page="1" />
        </section>
      </DragDropContext>
    </StyledDocument>
  );
};

export default ProposalPreviewForm;
